import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import { Toggle } from '@slite/components'
import { parseDomain } from '../../../utils/parse-domain'
import { TEAM_SIZE_OPTIONS, REMOTE_OPTIONS } from './constants'
import {
  setOrganizationDetails,
  goToTeamUrlForm,
  getUser,
  getOrganization,
} from './state'

import { Text, Form, Main } from '../../../components'
import { TeamNameInput } from '../../../components/Forms/TeamName'
import {
  ControlledSelect,
  SelectOption,
} from '../../../components/Select/ControlledSelect'
import { FormattedMessage, useIntl } from 'react-intl'
import { QUALIFIED_TEAM_KEY, setItemSafe } from '../../../helpers/localStorage'

type TeamNameForm = {
  size: SelectOption
  remoteStance: SelectOption
  membersCanAutoJoinByDomain: boolean
  name: string
}

export function TeamNameFormLayout() {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [logoURL, setLogoURL] = React.useState('')
  const user = useSelector(getUser)
  const organization = useSelector(getOrganization)

  const { domain, tld } = parseDomain(user.email ?? '') || {}
  const authDomains = `${domain}.${tld}`

  const { handleSubmit, formState, control, trigger } = useForm<TeamNameForm>({
    mode: 'onChange',
    defaultValues: {
      size: undefined,
      remoteStance: undefined,
      membersCanAutoJoinByDomain: !organization.isPopularDomain,
      name: organization.name ?? '',
    },
  })

  //initial validation for default values
  useEffect(() => {
    trigger('name')
  }, [trigger])

  useEffect(() => {
    if (!organization.isPopularDomain) {
      const url = `https://logo.clearbit.com/${authDomains}?size=300` // Clearbit causes CORS errors on 404
      const logo = new Image()
      logo.addEventListener('load', () => setLogoURL(url))
      logo.src = url
    }
  }, [authDomains, organization.isPopularDomain])

  const onSubmit = (data: TeamNameForm) => {
    const stepData = {
      name: data.name,
      size: data.size.value,
      photoURL: logoURL,
      membersCanAutoJoinByDomain: data.membersCanAutoJoinByDomain,
      remoteStance: data.remoteStance.value,
      authDomains: data.membersCanAutoJoinByDomain ? authDomains : '',
    }

    const isQualifiedTeam =
      !organization.isPopularDomain && parseInt(data.size.value, 10) > 1

    setItemSafe(QUALIFIED_TEAM_KEY, isQualifiedTeam ? 'true' : 'false')

    dispatch(setOrganizationDetails(stepData))
    dispatch(goToTeamUrlForm())
  }

  const ClearbitAttribution = logoURL && (
    <p className="shade-20 margin-top-16">
      <FormattedMessage
        id="boarding.createOrganization.logoAttribution"
        defaultMessage="Logo provided by {clearbitLink}"
        values={{
          clearbitLink: (
            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
            <a href="https://clearbit.com" className="shade-20">
              clearbit.com
            </a>
          ),
        }}
      />
    </p>
  )

  const teamSizeOptionsInternationalized = TEAM_SIZE_OPTIONS.map((option) => ({
    ...option,
    title: intl.formatMessage(option.title),
  }))

  const remoteOptionsInternationalized = REMOTE_OPTIONS.map((option) => ({
    ...option,
    title: intl.formatMessage(option.title),
    subtitle: intl.formatMessage(option.subtitle),
  }))

  return (
    <Main.Container footer={ClearbitAttribution}>
      <Main.Title>
        <FormattedMessage
          id="boarding.createOrg.form.title"
          defaultMessage="Let's set up your team"
          values={{}}
        />
      </Main.Title>
      <form
        className="flexbox flexbox-column form-grid"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div>
          <Form.Label>
            <FormattedMessage
              id="boarding.createOrg.form.teamName"
              defaultMessage="Company or team name"
              values={{}}
            />
          </Form.Label>
          <TeamNameInput control={control} name="name" logoURL={logoURL} />
        </div>

        <div>
          <Form.Label>
            <FormattedMessage
              id="boarding.createOrg.form.teamSize"
              defaultMessage="How many people will you be working with?"
              values={{}}
            />
          </Form.Label>
          <ControlledSelect
            control={control}
            rules={{ required: true }}
            name="size"
            placeholder={intl.formatMessage({
              id: 'boarding.createOrg.form.teamSize.placeholder',
              defaultMessage: 'Your team size',
            })}
            margin="both"
            options={teamSizeOptionsInternationalized}
          />
        </div>

        <div>
          <Form.Label>
            <FormattedMessage
              id="boarding.createOrg.form.remoteStance"
              defaultMessage="Does your company support remote work?"
              values={{}}
            />
          </Form.Label>
          <ControlledSelect
            control={control}
            rules={{ required: true }}
            name="remoteStance"
            placeholder={intl.formatMessage({
              id: 'boarding.createOrg.form.remoteStance.placeholder',
              defaultMessage: "Your company's stance on remote work",
            })}
            margin="both"
            options={remoteOptionsInternationalized}
          />
        </div>

        {!organization.isPopularDomain && (
          <div>
            <Controller
              control={control}
              name="membersCanAutoJoinByDomain"
              render={({ field }) => (
                <Text.Corpus className="margin-top-8 flexbox flexbox-align-center">
                  <Toggle
                    className="flexbox-no-shrink"
                    defaultChecked={field.value}
                    onChange={field.onChange}
                  />
                  <div className="margin-left-8 font-size-normal">
                    <FormattedMessage
                      id="boarding.createOrg.form.membersCanAutoJoinByDomain"
                      defaultMessage="Let anyone with @{authDomains} email join this team"
                      values={{
                        authDomains: authDomains || '',
                      }}
                    />
                  </div>
                </Text.Corpus>
              )}
            />
          </div>
        )}
        <Form.Button disabled={!formState.isValid}>
          <FormattedMessage
            id="boarding.createOrg.form.next"
            defaultMessage="Next"
            values={{}}
          />
        </Form.Button>
      </form>
    </Main.Container>
  )
}
